import React, { useMemo, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import { createTruncatedTextForTable } from '../../../utils/textUtils';
import { formatDateOnly } from '../../../utils/dateUtils';
import RequestSenderDropdown from '../createEditForm/components/RequestSenderDropdown';
import RequestBroadChannelDropdown from '../createEditForm/components/RequestBroadChannelDropdown';
import RequestTypeDropdown from '../createEditForm/components/RequestTypeDropdown';
import { selectUserParams } from '../../../store/slices/userParamsSlice';
import { useSelector } from 'react-redux';
import VorVehicleDropdown from '../createEditForm/components/VorVehicleDropdown';
import OldRequestFilterDropdown from './OldRequestFilterDropdown';

const STATUS_DRAFT = 5;
const STATUS_COMPLETED = 15;

export default function TeamProductivityMeasurementTable({ data, loading, onPage, lazyParams, totalCount, activeFilters, onFiltersChange, sortField, sortOrder, onSortChange, onResetFilters, onEdit }) {
    const { th } = useLanguage();
    const dataTableRef = useRef(null);
    const userParams = useSelector(selectUserParams);
    const currentUserId = userParams?.userId;

    const rows = Array.isArray(data) ? data.map((item, index) => ({ ...item, _rowId: `${lazyParams.first}-${index}` })) : [];

    const textMatchModeOptions = useMemo(
        () => [
            { label: th('filter.startsWith'), value: FilterMatchMode.STARTS_WITH },
            { label: th('filter.contains'), value: FilterMatchMode.CONTAINS },
            { label: th('filter.notContains'), value: FilterMatchMode.NOT_CONTAINS },
            { label: th('filter.endsWith'), value: FilterMatchMode.ENDS_WITH },
            { label: th('filter.equals'), value: FilterMatchMode.EQUALS },
            { label: th('filter.notEquals'), value: FilterMatchMode.NOT_EQUALS }
        ],
        [th]
    );

    const tableFilters = useMemo(
        () => ({
            requestNumber: { value: activeFilters?.requestNumber ?? null, matchMode: activeFilters?.requestNumberMatchMode ?? FilterMatchMode.CONTAINS },
            requestBroadChannelText: { value: activeFilters?.requestBroadChannelText ?? null, matchMode: FilterMatchMode.EQUALS },
            requestTypeText: { value: activeFilters?.requestTypeText ?? null, matchMode: FilterMatchMode.EQUALS },
            requestDetail: { value: activeFilters?.requestDetail ?? null, matchMode: activeFilters?.requestDetailMatchMode ?? FilterMatchMode.CONTAINS },
            requestSenderText: { value: activeFilters?.requestSenderText ?? null, matchMode: FilterMatchMode.EQUALS },
            requestSenderName: { value: activeFilters?.requestSenderName ?? null, matchMode: activeFilters?.requestSenderNameMatchMode ?? FilterMatchMode.CONTAINS },
            howItConcluded: { value: activeFilters?.howItConcluded ?? null, matchMode: activeFilters?.howItConcludedMatchMode ?? FilterMatchMode.CONTAINS },
            lessonLearned: { value: activeFilters?.lessonLearned ?? null, matchMode: activeFilters?.lessonLearnedMatchMode ?? FilterMatchMode.CONTAINS },
            startDate: { value: activeFilters?.startDate ?? null, matchMode: FilterMatchMode.DATE_IS },
            endDate: { value: activeFilters?.endDate ?? null, matchMode: FilterMatchMode.DATE_IS },
            createdName: { value: activeFilters?.createdName ?? null, matchMode: activeFilters?.createdNameMatchMode ?? FilterMatchMode.CONTAINS },
            createdDate: { value: activeFilters?.createdDate ?? null, matchMode: FilterMatchMode.DATE_IS },
            vorVehicleText: { value: activeFilters?.vorVehicleText ?? null, matchMode: FilterMatchMode.EQUALS },
            oldRequestNumbers: { value: activeFilters?.oldRequestNumbers ?? null, matchMode: FilterMatchMode.EQUALS },
            timeDifference: { value: activeFilters?.timeDifference ?? null, matchMode: activeFilters?.timeDifferenceMatchMode ?? FilterMatchMode.CONTAINS },
            status: {
                value: activeFilters?.status ?? null,
                matchMode: FilterMatchMode.EQUALS
            }
        }),
        [activeFilters]
    );

    const handleTableFilter = (e) => {
        const filters = e?.filters || {};
        const getVal = (field) => (field in filters ? filters[field]?.value ?? null : activeFilters?.[field] ?? null);
        const getMode = (field, defaultMode = FilterMatchMode.CONTAINS) => (field in filters ? filters[field]?.matchMode ?? defaultMode : activeFilters?.[`${field}MatchMode`] ?? defaultMode);

        onFiltersChange?.({
            ...activeFilters,
            requestNumber: getVal('requestNumber'),
            requestNumberMatchMode: getMode('requestNumber'),
            requestBroadChannelText: getVal('requestBroadChannelText'),
            requestBroadChannelTextMatchMode: FilterMatchMode.EQUALS,
            requestTypeText: getVal('requestTypeText'),
            requestTypeTextMatchMode: FilterMatchMode.EQUALS,
            requestDetail: getVal('requestDetail'),
            requestDetailMatchMode: getMode('requestDetail'),
            requestSenderText: getVal('requestSenderText'),
            requestSenderTextMatchMode: FilterMatchMode.EQUALS,
            requestSenderName: getVal('requestSenderName'),
            requestSenderNameMatchMode: getMode('requestSenderName'),
            howItConcluded: getVal('howItConcluded'),
            howItConcludedMatchMode: getMode('howItConcluded'),
            lessonLearned: getVal('lessonLearned'),
            lessonLearnedMatchMode: getMode('lessonLearned'),
            startDate: getVal('startDate'),
            startDateMatchMode: FilterMatchMode.DATE_IS,
            endDate: getVal('endDate'),
            endDateMatchMode: FilterMatchMode.DATE_IS,
            createdName: getVal('createdName'),
            createdNameMatchMode: getMode('createdName'),
            createdDate: getVal('createdDate'),
            createdDateMatchMode: FilterMatchMode.DATE_IS,
            status: getVal('status'),
            statusMatchMode: FilterMatchMode.EQUALS,
            vorVehicleText: getVal('vorVehicleText'),
            vorVehicleTextMatchMode: getMode('vorVehicleText'),
            oldRequestNumbers: getVal('oldRequestNumbers'),
            oldRequestNumbersMatchMode: FilterMatchMode.EQUALS,
            timeDifference: getVal('timeDifference'),
            timeDifferenceMatchMode: getMode('timeDifference')
        });
    };

    const dateFilterTemplate = (options) => (
        <Calendar
            value={options.value}
            onChange={(e) => {
                let normalized = e.value;
                if (normalized instanceof Date) {
                    normalized = new Date(normalized.getFullYear(), normalized.getMonth(), normalized.getDate());
                }
                options.filterApplyCallback(normalized);
            }}
            dateFormat="dd.mm.yy"
            placeholder={th('common.search')}
            showIcon
            showButtonBar
            className="p-column-filter"
            style={{ minWidth: '12rem' }}
        />
    );

    const requestSenderFilterTemplate = (options) => <RequestSenderDropdown value={options.value} onChange={(val) => options.filterApplyCallback(val)} className="p-column-filter w-full" />;

    const requestBroadChannelFilterTemplate = (options) => <RequestBroadChannelDropdown value={options.value} onChange={(val) => options.filterApplyCallback(val)} className="p-column-filter w-full" />;

    const requestTypeFilterTemplate = (options) => <RequestTypeDropdown value={options.value} onChange={(val) => options.filterApplyCallback(val)} className="p-column-filter w-full" />;

    const statusBodyTemplate = (rowData) => {
        const isCompleted = rowData.status === STATUS_COMPLETED;
        return (
            <span className={`px-2 py-1 border-round text-xs font-semibold ${isCompleted ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'}`}>
                {isCompleted ? th('teamProductivity.status.completed') : th('teamProductivity.status.draft')}
            </span>
        );
    };

    const actionFilterTemplate = () => (
        <div className="flex justify-content-center align-items-center">
            <i onClick={onResetFilters} className="pi pi-filter-slash cursor-pointer" style={{ color: '#0B3187', fontSize: '1rem' }} title={th('filter.reset')} />
        </div>
    );

    const createTextColumn = (field, headerKey, options = {}) => {
        const { minWidth = '150px', body } = options;
        return (
            <Column
                key={field}
                sortable
                field={field}
                header={th(headerKey)}
                style={{ minWidth }}
                body={body}
                filter
                filterField={field}
                filterPlaceholder={th('common.search')}
                showFilterMenu
                filterMatchModeOptions={textMatchModeOptions}
                showClearButton={false}
            />
        );
    };

    const createDropdownColumn = (field, headerKey, filterElement, options = {}) => {
        const { body, sortable = true, minWidth = '150px' } = options;
        return <Column key={field} sortable={sortable} field={field} header={th(headerKey)} style={{ minWidth }} body={body} filter filterField={field} filterElement={filterElement} showFilterMenu={false} showClearButton={false} />;
    };

    const createDateColumn = (field, headerKey) => (
        <Column
            key={field}
            sortable
            field={field}
            header={th(headerKey)}
            style={{ minWidth: '160px' }}
            body={(row) => {
                if (!row?.[field]) return '-';
                const date = new Date(row[field]);
                const datePart = formatDateOnly(row[field]);
                const timePart = date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
                return `${datePart} ${timePart}`;
            }}
            filter
            filterField={field}
            filterElement={dateFilterTemplate}
            showFilterMenu={false}
            showClearButton={false}
        />
    );
    const statusFilterTemplate = (options) => (
        <Dropdown
            value={options.value}
            onChange={(e) => options.filterApplyCallback(e.value)}
            options={[
                { label: th('teamProductivity.status.draft'), value: 5 },
                { label: th('teamProductivity.status.completed'), value: 15 }
            ]}
            placeholder={th('common.search')}
            className="p-column-filter w-full"
            showClear
        />
    );
    const vorVehicleFilterTemplate = (options) => <VorVehicleDropdown value={options.value} onChange={(val) => options.filterApplyCallback(val)} className="p-column-filter w-full" />;
    const oldRequestFilterTemplate = (options) => <OldRequestFilterDropdown value={options.value} onChange={(val) => options.filterApplyCallback(val)} className="p-column-filter w-full" />;
    return (
        <DataTable
            value={rows}
            dataKey="_rowId"
            lazy
            paginator
            first={lazyParams.first}
            rows={lazyParams.rows || 10}
            totalRecords={totalCount}
            onPage={onPage}
            loading={loading}
            filterDisplay="row"
            filters={tableFilters}
            onFilter={handleTableFilter}
            emptyMessage={th('common.noRecords')}
            rowsPerPageOptions={[10, 25, 50]}
            showGridlines
            tableStyle={{ minWidth: '60rem' }}
            ref={dataTableRef}
            onSort={onSortChange}
            sortField={sortField}
            sortOrder={sortOrder}
            sortMode="single"
        >
            <Column
                key="action"
                body={(rowData) => {
                    const isCompleted = rowData.status === STATUS_COMPLETED;

     
                    if (isCompleted) {
                        return <i className="pi pi-eye" style={{ color: '#0B3187', cursor: 'pointer' }} onClick={() => onEdit(rowData)} title={th('common.view')} />;
                    }


                    const isOwner = rowData.createdBy === currentUserId;
                    return (
                        <i
                            className="pi pi-pencil"
                            style={{
                                color: isOwner ? '#0B3187' : '#bdbdbd',
                                cursor: isOwner ? 'pointer' : 'not-allowed',
                                opacity: isOwner ? 1 : 0.5
                            }}
                            onClick={isOwner ? () => onEdit(rowData) : undefined}
                            title={isOwner ? th('common.edit') : th('teamProductivity.messages.onlyOwnerCanEdit')}
                        />
                    );
                }}
                bodyClassName="text-center"
                headerClassName="text-center"
                exportable={false}
                style={{ width: '50px', minWidth: '50px' }}
                sortable={false}
                filter={false}
                showFilterMenu={false}
            />
            {createTextColumn('requestNumber', 'teamProductivity.table.requestNumber', { minWidth: '100px' })}
            {createDropdownColumn('requestBroadChannelText', 'teamProductivity.table.requestBroadChannel', requestBroadChannelFilterTemplate)}
            {createDropdownColumn('requestTypeText', 'teamProductivity.table.requestType', requestTypeFilterTemplate)}
            {createTextColumn('requestDetail', 'teamProductivity.table.requestDetail', {
                minWidth: '200px',
                body: (row) => createTruncatedTextForTable(row.requestDetail, 40, `detail-${row.id}`)
            })}
            {createDateColumn('startDate', 'teamProductivity.table.startDate')}
            {createDateColumn('endDate', 'teamProductivity.table.endDate')}

            {createTextColumn('timeDifference', 'teamProductivity.table.timeDifference', {
                minWidth: '120px',
                body: (row) => (row.timeDifference != null ? `${Number(row.timeDifference).toFixed(2)}` : '-')
            })}

            {createDropdownColumn('requestSenderText', 'teamProductivity.table.requestSender', requestSenderFilterTemplate)}
            {createTextColumn('requestSenderName', 'teamProductivity.table.requestSenderName', {
                minWidth: '180px',
                body: (row) => (row.requestSenderName ? createTruncatedTextForTable(String(row.requestSenderName), 40, `sender-${row.id}`) : '-')
            })}

            <Column
                key="oldRequestNumbers"
                sortable={false}
                field="oldRequestNumbers"
                header={th('teamProductivity.table.oldRequestNumbers')}
                style={{ minWidth: '150px' }}
                body={(row) => {
                    if (!row.oldRequestNumbers || row.oldRequestNumbers.length === 0) return '-';
                    return (
                        <div className="flex flex-wrap gap-1">
                            {row.oldRequestNumbers.map((num, index) => (
                                <span key={index} style={{ backgroundColor: '#e9ecef', color: '#495057', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', border: '1px solid #ced4da' }}>
                                    {num}
                                </span>
                            ))}
                        </div>
                    );
                }}
                filter
                filterField="oldRequestNumbers"
                filterElement={oldRequestFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />

            {createTextColumn('howItConcluded', 'teamProductivity.table.howItConcluded', {
                minWidth: '200px',
                body: (row) => createTruncatedTextForTable(row.howItConcluded, 40, `concluded-${row.id}`)
            })}
            {createTextColumn('lessonLearned', 'teamProductivity.table.lessonLearned', {
                minWidth: '200px',
                body: (row) => createTruncatedTextForTable(row.lessonLearned, 40, `lesson-${row.id}`)
            })}
            {createDropdownColumn('vorVehicleText', 'teamProductivity.table.vorVehicle', vorVehicleFilterTemplate)}

            <Column
                key="status"
                sortable
                field="status"
                header={th('teamProductivity.table.status')}
                style={{ minWidth: '130px' }}
                body={statusBodyTemplate}
                filter
                filterField="status"
                filterElement={statusFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />

            {createTextColumn('createdName', 'teamProductivity.table.createdName')}
            <Column
                key="createdDate"
                sortable
                field="createdDate"
                header={th('teamProductivity.table.createdDate')}
                style={{ minWidth: '140px' }}
                body={(row) => (row?.createdDate ? formatDateOnly(row.createdDate) : '-')}
                filter
                filterField="createdDate"
                filterElement={dateFilterTemplate}
                showFilterMenu={false}
                showClearButton={false}
            />
        </DataTable>
    );
}
